import React from "react";
import { Box, Typography } from "@mui/material";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ModalShell from "./ModalShell";
import { CollectionReport } from "../../../api/derin";
import { collectionColors, font, formatFileSize, subtitleSx } from "./tokens";

interface RemoveReportDialogProps {
  open: boolean;
  report: CollectionReport | null;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting?: boolean;
}

/**
 * Removing a report is the one delete in this tab that destroys something: the
 * configuration and About files are replaced by the next upload, but a report's stored
 * file is gone and has to be uploaded again from wherever the researcher keeps it.
 */
const RemoveReportDialog: React.FC<RemoveReportDialogProps> = ({
  open,
  report,
  onClose,
  onConfirm,
  isSubmitting = false,
}) => (
  <ModalShell
    open={open}
    onClose={onClose}
    title="Remove report"
    subtitle={
      report
        ? `"${report.title}" stops being available to the client and its stored file is deleted. Uploading it again means picking the file from your own copy.`
        : undefined
    }
    confirmLabel="Remove report"
    onConfirm={onConfirm}
    isSubmitting={isSubmitting}
    danger
  >
    {report && (
      <Box
        sx={{
          display: "flex",
          gap: "10px",
          padding: "12px",
          background: collectionColors.dangerSurface,
          borderRadius: "8px",
        }}
      >
        <WarningAmberOutlinedIcon sx={{ width: 18, height: 18, color: collectionColors.danger, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: font,
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "20px",
              color: collectionColors.danger,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {report.fileName}
          </Typography>
          <Typography sx={{ ...subtitleSx, fontSize: "13px", lineHeight: "18px" }}>
            {formatFileSize(report.sizeBytes)} — this file is deleted, not archived.
          </Typography>
        </Box>
      </Box>
    )}
    {report && <Typography sx={subtitleSx}>Report ID: {report.id}</Typography>}
  </ModalShell>
);

export default RemoveReportDialog;
